import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getApiUrl } from '../config/api';
import { createLogger } from '@metron/shared';

const logger = createLogger('DatabaseStatusBanner');

interface SystemStatus {
  environment: string;
  database: {
    name: string;
    host: string;
    port: number;
    user: string;
    isLocal: boolean;
    isDocker: boolean;
  };
  timestamp: string;
}

export function DatabaseStatusBanner() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(getApiUrl('system/status'))
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data: SystemStatus) => {
        setStatus(data);
        setIsLoading(false);
      })
      .catch((error) => {
        logger.error({ error }, 'Failed to fetch system status');
        setIsLoading(false);
      });
  }, []);

  // Don't show anything while loading
  if (isLoading || !status) {
    return null;
  }

  // Only show banner in development (localhost)
  const isDevelopment = import.meta.env.DEV;
  if (!isDevelopment) {
    return null;
  }

  // Show green banner for local databases
  if (status.database.isLocal) {
    const dockerInfo = status.database.isDocker ? ' (Docker)' : '';
    return (
      <div className='mb-6 flex items-center gap-2 rounded-lg border border-green-600 bg-green-50 dark:bg-green-950 px-4 py-2'>
        <CheckCircle2 className='h-4 w-4 text-green-600 flex-shrink-0' />
        <p className='text-sm text-green-600'>
          Connected to local database:{' '}
          <strong className='text-green-700 dark:text-green-500'>{status.database.name}</strong> on
          {' '}
          <strong className='text-green-700 dark:text-green-500'>{status.database.host}</strong>
          {dockerInfo}
        </p>
      </div>
    );
  }

  // Show warning banner for remote databases when running locally
  return (
    <div className='mb-6 flex items-center gap-2 rounded-lg border border-destructive bg-destructive/10 px-4 py-2'>
      <AlertTriangle className='h-4 w-4 text-destructive flex-shrink-0' />
      <p className='text-sm text-destructive'>
        Connected to remote database:{' '}
        <strong className='text-destructive'>{status.database.name}</strong> at{' '}
        <strong className='text-destructive'>{status.database.host}</strong> - Use with caution
      </p>
    </div>
  );
}
